import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../utils/axios-auth';
import { getUserFacingMessageFromAxiosError, toUserFacingMessage } from '../utils/user-facing-api-error';
// Async thunks
export const fetchStudentSkillAnalysis = createAsyncThunk('progressMonitoring/fetchStudentSkillAnalysis', async ({ studentId }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/progress/students/${studentId}/skill-analysis`);
        if (response.data.success) {
            return response.data.data;
        }
        else {
            return rejectWithValue(toUserFacingMessage(response.data?.message, { fallback: 'Failed to fetch skill analysis' }));
        }
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to fetch skill analysis' }));
    }
});
export const fetchLearningRecommendations = createAsyncThunk('progressMonitoring/fetchLearningRecommendations', async ({ studentId, limit = 5 }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/progress/students/${studentId}/recommendations`, {
            params: { limit }
        });
        if (response.data.success) {
            return response.data.data.recommendations;
        }
        else {
            return rejectWithValue(toUserFacingMessage(response.data?.message, { fallback: 'Failed to fetch recommendations' }));
        }
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to fetch recommendations' }));
    }
});
export const fetchTutorWarnings = createAsyncThunk('progressMonitoring/fetchTutorWarnings', async ({ tutorId, status = 'active', page = 1, limit = 20 }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/progress/tutors/${tutorId}/warnings`, {
            params: { status, page, limit }
        });
        if (response.data.success) {
            return response.data.data;
        }
        else {
            return rejectWithValue(toUserFacingMessage(response.data?.message, { fallback: 'Failed to fetch warnings' }));
        }
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to fetch warnings' }));
    }
});
export const createIntervention = createAsyncThunk('progressMonitoring/createIntervention', async (interventionData, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post('/progress/interventions', interventionData);
        if (response.data.success) {
            return response.data.data;
        }
        else {
            return rejectWithValue(toUserFacingMessage(response.data?.message, { fallback: 'Failed to create intervention' }));
        }
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to create intervention' }));
    }
});
export const updateIntervention = createAsyncThunk('progressMonitoring/updateIntervention', async ({ interventionId, ...updateData }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.put(`/progress/interventions/${interventionId}`, updateData);
        if (response.data.success) {
            return response.data.data;
        }
        else {
            return rejectWithValue(toUserFacingMessage(response.data?.message, { fallback: 'Failed to update intervention' }));
        }
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to update intervention' }));
    }
});
export const fetchStudentInterventions = createAsyncThunk('progressMonitoring/fetchStudentInterventions', async ({ studentId, status, page = 1, limit = 20 }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/progress/students/${studentId}/interventions`, {
            params: { status, page, limit }
        });
        if (response.data.success) {
            return response.data.data;
        }
        else {
            return rejectWithValue(toUserFacingMessage(response.data?.message, { fallback: 'Failed to fetch interventions' }));
        }
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to fetch interventions' }));
    }
});
export const logStudentActivity = createAsyncThunk('progressMonitoring/logStudentActivity', async (activityData, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post('/progress/activity/log', activityData);
        if (response.data.success) {
            return response.data.data;
        }
        else {
            return rejectWithValue(toUserFacingMessage(response.data?.message, { fallback: 'Failed to log activity' }));
        }
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to log activity' }));
    }
});
// Enhanced progress monitoring thunks
export const fetchStudentProgressSummary = createAsyncThunk('progressMonitoring/fetchStudentProgressSummary', async ({ studentId, classroomId, days }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/progress/students/${studentId}/progress-summary`, {
            params: {
                classroomId,
                ...(days != null && days > 0 ? { days } : {}),
            },
        });
        if (response.data.success) {
            return response.data.data;
        }
        else {
            return rejectWithValue(toUserFacingMessage(response.data?.message, { fallback: 'Failed to fetch progress summary' }));
        }
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to fetch progress summary' }));
    }
});
export const updateStudentProgress = createAsyncThunk('progressMonitoring/updateStudentProgress', async ({ studentId, classroomId, progressData }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.put(`/progress/students/${studentId}/progress`, {
            classroomId,
            progressData
        });
        if (response.data.success) {
            return response.data.data;
        }
        else {
            return rejectWithValue(toUserFacingMessage(response.data?.message, { fallback: 'Failed to update progress' }));
        }
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to update progress' }));
    }
});
export const fetchLearningRecommendationsEnhanced = createAsyncThunk('progressMonitoring/fetchLearningRecommendationsEnhanced', async ({ studentId, classroomId, limit = 5 }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/progress/students/${studentId}/recommendations-enhanced`, {
            params: { classroomId, limit }
        });
        if (response.data.success) {
            return response.data.data;
        }
        else {
            return rejectWithValue(toUserFacingMessage(response.data?.message, { fallback: 'Failed to fetch enhanced recommendations' }));
        }
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to fetch enhanced recommendations' }));
    }
});
export const flagAtRiskStudent = createAsyncThunk('progressMonitoring/flagAtRiskStudent', async ({ studentId, triggeringRule, details }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/progress/students/${studentId}/flag-at-risk`, {
            triggeringRule,
            details
        });
        if (response.data.success) {
            return response.data.data;
        }
        else {
            return rejectWithValue(toUserFacingMessage(response.data?.message, { fallback: 'Failed to flag student as at-risk' }));
        }
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to flag student as at-risk' }));
    }
});
export const fetchProgressTrends = createAsyncThunk('progressMonitoring/fetchProgressTrends', async ({ studentId, classroomId, days = 30 }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/progress/students/${studentId}/progress-trends`, {
            params: { classroomId, days }
        });
        if (response.data.success) {
            return response.data.data;
        }
        else {
            return rejectWithValue(toUserFacingMessage(response.data?.message, { fallback: 'Failed to fetch progress trends' }));
        }
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to fetch progress trends' }));
    }
});
// Initial state
const initialState = {
    skillAnalysis: null,
    learningRecommendations: [],
    progressSummary: null,
    progressTrends: null,
    enhancedRecommendations: null,
    warnings: [],
    warningsPagination: {
        page: 1,
        limit: 20,
        total: 0
    },
    interventions: [],
    interventionsPagination: {
        page: 1,
        limit: 20,
        total: 0
    },
    loading: {
        skillAnalysis: false,
        recommendations: false,
        progressSummary: false,
        progressTrends: false,
        enhancedRecommendations: false,
        warnings: false,
        interventions: false,
        creatingIntervention: false,
        updatingIntervention: false,
        flaggingAtRisk: false
    },
    error: {
        skillAnalysis: null,
        recommendations: null,
        progressSummary: null,
        progressTrends: null,
        enhancedRecommendations: null,
        warnings: null,
        interventions: null,
        flaggingAtRisk: null
    }
};
// Slice
const progressMonitoringSlice = createSlice({
    name: 'progressMonitoring',
    initialState,
    reducers: {
        clearSkillAnalysis: (state) => {
            state.skillAnalysis = null;
            state.error.skillAnalysis = null;
        },
        clearRecommendations: (state) => {
            state.learningRecommendations = [];
            state.error.recommendations = null;
        },
        clearWarnings: (state) => {
            state.warnings = [];
            state.error.warnings = null;
        },
        clearInterventions: (state) => {
            state.interventions = [];
            state.error.interventions = null;
        },
        clearAllErrors: (state) => {
            state.error = {
                skillAnalysis: null,
                recommendations: null,
                progressSummary: null,
                progressTrends: null,
                enhancedRecommendations: null,
                warnings: null,
                interventions: null,
                flaggingAtRisk: null
            };
        }
    },
    extraReducers: (builder) => {
        builder
            // Skill Analysis
            .addCase(fetchStudentSkillAnalysis.pending, (state) => {
            state.loading.skillAnalysis = true;
            state.error.skillAnalysis = null;
        })
            .addCase(fetchStudentSkillAnalysis.fulfilled, (state, action) => {
            state.loading.skillAnalysis = false;
            state.skillAnalysis = action.payload;
            state.error.skillAnalysis = null;
        })
            .addCase(fetchStudentSkillAnalysis.rejected, (state, action) => {
            state.loading.skillAnalysis = false;
            state.error.skillAnalysis = action.payload || 'Failed to fetch skill analysis';
        })
            // Learning Recommendations
            .addCase(fetchLearningRecommendations.pending, (state) => {
            state.loading.recommendations = true;
            state.error.recommendations = null;
        })
            .addCase(fetchLearningRecommendations.fulfilled, (state, action) => {
            state.loading.recommendations = false;
            state.learningRecommendations = action.payload;
            state.error.recommendations = null;
        })
            .addCase(fetchLearningRecommendations.rejected, (state, action) => {
            state.loading.recommendations = false;
            state.error.recommendations = action.payload || 'Failed to fetch recommendations';
        })
            // Warnings
            .addCase(fetchTutorWarnings.pending, (state) => {
            state.loading.warnings = true;
            state.error.warnings = null;
        })
            .addCase(fetchTutorWarnings.fulfilled, (state, action) => {
            state.loading.warnings = false;
            state.warnings = action.payload.warnings;
            state.warningsPagination = action.payload.pagination;
            state.error.warnings = null;
        })
            .addCase(fetchTutorWarnings.rejected, (state, action) => {
            state.loading.warnings = false;
            state.error.warnings = action.payload || 'Failed to fetch warnings';
        })
            // Create Intervention
            .addCase(createIntervention.pending, (state) => {
            state.loading.creatingIntervention = true;
        })
            .addCase(createIntervention.fulfilled, (state, action) => {
            state.loading.creatingIntervention = false;
            state.interventions.unshift(action.payload.intervention);
        })
            .addCase(createIntervention.rejected, (state, action) => {
            state.loading.creatingIntervention = false;
            state.error.interventions = action.payload || 'Failed to create intervention';
        })
            // Update Intervention
            .addCase(updateIntervention.pending, (state) => {
            state.loading.updatingIntervention = true;
        })
            .addCase(updateIntervention.fulfilled, (state, action) => {
            state.loading.updatingIntervention = false;
            const index = state.interventions.findIndex(i => i.id === action.payload.intervention.id);
            if (index !== -1) {
                state.interventions[index] = action.payload.intervention;
            }
        })
            .addCase(updateIntervention.rejected, (state, action) => {
            state.loading.updatingIntervention = false;
            state.error.interventions = action.payload || 'Failed to update intervention';
        })
            // Student Interventions
            .addCase(fetchStudentInterventions.pending, (state) => {
            state.loading.interventions = true;
            state.error.interventions = null;
        })
            .addCase(fetchStudentInterventions.fulfilled, (state, action) => {
            state.loading.interventions = false;
            state.interventions = action.payload.interventions;
            state.interventionsPagination = action.payload.pagination;
            state.error.interventions = null;
        })
            .addCase(fetchStudentInterventions.rejected, (state, action) => {
            state.loading.interventions = false;
            state.error.interventions = action.payload || 'Failed to fetch interventions';
        })
            // Enhanced Progress Monitoring
            .addCase(fetchStudentProgressSummary.pending, (state) => {
            state.loading.progressSummary = true;
            state.error.progressSummary = null;
        })
            .addCase(fetchStudentProgressSummary.fulfilled, (state, action) => {
            state.loading.progressSummary = false;
            state.progressSummary = action.payload;
            state.error.progressSummary = null;
        })
            .addCase(fetchStudentProgressSummary.rejected, (state, action) => {
            state.loading.progressSummary = false;
            state.error.progressSummary = action.payload || 'Failed to fetch progress summary';
        })
            .addCase(updateStudentProgress.pending, (state) => {
            state.loading.updatingIntervention = true; // Reusing loading state for consistency
        })
            .addCase(updateStudentProgress.fulfilled, (state, action) => {
            state.loading.updatingIntervention = false;
            // No direct state update for progressSummary here, as it's a summary, not an intervention
        })
            .addCase(updateStudentProgress.rejected, (state, action) => {
            state.loading.updatingIntervention = false;
            state.error.progressSummary = action.payload || 'Failed to update progress';
        })
            .addCase(fetchLearningRecommendationsEnhanced.pending, (state) => {
            state.loading.enhancedRecommendations = true;
            state.error.enhancedRecommendations = null;
        })
            .addCase(fetchLearningRecommendationsEnhanced.fulfilled, (state, action) => {
            state.loading.enhancedRecommendations = false;
            state.enhancedRecommendations = action.payload;
            state.error.enhancedRecommendations = null;
        })
            .addCase(fetchLearningRecommendationsEnhanced.rejected, (state, action) => {
            state.loading.enhancedRecommendations = false;
            state.error.enhancedRecommendations = action.payload || 'Failed to fetch enhanced recommendations';
        })
            .addCase(flagAtRiskStudent.pending, (state) => {
            state.loading.flaggingAtRisk = true;
        })
            .addCase(flagAtRiskStudent.fulfilled, (state, action) => {
            state.loading.flaggingAtRisk = false;
            // No direct state update for warnings or interventions here, as it's a separate action
        })
            .addCase(flagAtRiskStudent.rejected, (state, action) => {
            state.loading.flaggingAtRisk = false;
            state.error.flaggingAtRisk = action.payload || 'Failed to flag student as at-risk';
        })
            .addCase(fetchProgressTrends.pending, (state) => {
            state.loading.progressTrends = true;
            state.error.progressTrends = null;
        })
            .addCase(fetchProgressTrends.fulfilled, (state, action) => {
            state.loading.progressTrends = false;
            state.progressTrends = action.payload;
            state.error.progressTrends = null;
        })
            .addCase(fetchProgressTrends.rejected, (state, action) => {
            state.loading.progressTrends = false;
            state.error.progressTrends = action.payload || 'Failed to fetch progress trends';
        });
    }
});
export const { clearSkillAnalysis, clearRecommendations, clearWarnings, clearInterventions, clearAllErrors } = progressMonitoringSlice.actions;
export default progressMonitoringSlice.reducer;
